import { Controller, useForm } from 'react-hook-form';
import { Button } from 'src/components/ui/button';
import React, { useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  booleanScheme,
  emailScheme,
  momentScheme,
  numberScheme,
  passwordScheme,
  stringScheme,
} from 'src/utils/validation-schemes';
import {
  useCreateKnowledgeBaseCollectorMutation,
  useGetKnowledgeBaseCollectorsQuery,
  useLazyGetKnowledgeBaseCollectorsQuery,
  useLazyGetKnowledgeBaseCollectorZendeskConsentLinkQuery,
  useRunKnowledgeBaseCollectorMutation,
  useUpdateKnowledgeBaseCollectorMutation,
} from 'src/services/knowledgeBaseService';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { MaskedDateInput } from 'src/components/MaskedDateInput/MaskedDateInput';
import moment from 'moment';
import {
  AUTH_STATUS_POLL_INTERVAL_MS,
  AUTH_STATUS_POLL_TIMEOUT_MS,
  ISO_DATE_FORMAT,
} from 'src/shared/constants';
import Input from 'src/components/Input/Input';
import { Switch } from 'src/components/ui/switch';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import {
  WEBSITE_INTERVAL,
  ZENDESK_TICKETS_INITIAL_REQUEST,
  TICKETS_INTERVAL,
} from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';
import { generateUniqueName, INTEGRATION_NAMES_MAP } from 'src/utils/fileUtils';
import { ZENDESK_HELP_CENTER_URL } from 'src/constants/urls';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { useAppSelector } from 'src/store';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';
import { splitString } from 'src/utils/splitString';
import { Spinner } from 'src/components/Spinner/Spinner';
import {
  COLLAPSIBLE_CONTENT_CLASS_NAME,
  COLLAPSIBLE_TRIGGER_CLASS_NAME,
  ZendeskConnectSection,
} from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskConnectSection';
import { Collapsible } from 'src/components/ui/collapsible';
import ChevronDownIcon from 'src/assets/img/chevron-down-icon.svg';
import ArrowLargeGreyIcon from 'src/assets/img/arrow-large-grey-icon.svg';

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const INTERVAL_OPTIONS = [
  { value: TICKETS_INTERVAL.WEEKLY, id: 'interval-weekly', label: 'Weekly' },
  { value: TICKETS_INTERVAL.MONTHLY, id: 'interval-monthly', label: 'Monthly' },
];

export const ZendeskTicketsForm = ({
  zendeskIntegrationType,
  prevStep = () => {},
  closePopup = (..._args: any[]) => {},
  backButtonVisible = true,
  knowledgeBaseId = null,
  initialRequest = {} as any,
}: {
  zendeskIntegrationType?: any;
  prevStep?: () => void;
  closePopup?: (...args: any[]) => void;
  backButtonVisible?: boolean;
  knowledgeBaseId?: any;
  initialRequest?: any;
}) => {
  const { id } = useParams();
  const notification = useNotification();
  const loader = useLoader();
  const [
    createCollector,
    {
      data: createCollectorData,
      isLoading: isLoadingCreateCollector,
      isError: isErrorCreateCollector,
      isSuccess: isSuccessCreateCollector,
    },
  ] = useCreateKnowledgeBaseCollectorMutation() as any;
  const { data: collectorsResponse, isLoading: isLoadingCollectors } =
    useGetKnowledgeBaseCollectorsQuery(
      {
        id: knowledgeBaseId || id,
        include_data: false,
      },
      {
        skip: !(knowledgeBaseId || id),
      },
    );
  const { isTrialPlan } = useAppSelector((s) => s.planSlice);
  // Separate instance so creating the collector from "Connect with Zendesk" doesn't trigger
  // the Integrate success flow (notification and closing the popup).
  const [createConnectCollector] = useCreateKnowledgeBaseCollectorMutation();
  const [updateCollector] = useUpdateKnowledgeBaseCollectorMutation();
  const [runCollector] = useRunKnowledgeBaseCollectorMutation();
  const [getZendeskConsentLink] = useLazyGetKnowledgeBaseCollectorZendeskConsentLinkQuery();
  const [getCollectors] = useLazyGetKnowledgeBaseCollectorsQuery();
  const kbId = knowledgeBaseId || id;
  const [isManualConnectOpen, setIsManualConnectOpen] = useState(false);
  // The collector created by "Connect with Zendesk"; Integrate updates it instead of creating another.
  const [connectedCollector, setConnectedCollector] = useState(null);
  const [isCheckingAuthStatus, setIsCheckingAuthStatus] = useState(false);
  const isMountedRef = useRef(true);
  const isPollingAuthStatusRef = useRef(false);
  // Once Zendesk OAuth is authorized, the manual API token route is locked.
  const isZendeskAuthorized =
    connectedCollector?.auth_type === 'oauth' && connectedCollector?.auth_status === 'ok';
  const isManualConnectActive = isManualConnectOpen && !isZendeskAuthorized;

  let type = null;

  switch (zendeskIntegrationType) {
    case 'Help Center':
      type = 'zendesk_helpcenter';
      break;
    case 'Tickets':
      type = 'zendesk_tickets';
      break;
  }

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isSuccessCreateCollector) {
      notification.success(`${createCollectorData.name} Added Successfully.`);
      close(true);
    }
  }, [isSuccessCreateCollector]);

  useEffect(() => {
    if (isLoadingCreateCollector && isLoadingCollectors) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingCreateCollector, isLoadingCollectors]);

  useEffect(() => {
    if (isErrorCreateCollector) {
      close(true);
    }
  }, [isErrorCreateCollector]);

  const prev = () => {
    prevStep();
  };

  const close = (shouldCloseSourceModal = false) => {
    closePopup(shouldCloseSourceModal);
  };

  const {
    handleSubmit,
    control,
    watch,
    formState: { isValid },
  } = useForm<any>({
    defaultValues: {
      start_from: '',
      agents: '',
      tags: '',
      groups: '',
      type: type,
      autoSync: !isTrialPlan,
      interval: WEBSITE_INTERVAL.MONTHLY,
      subdomain: '',
      auth_email: '',
      api_token: '',
    },
    resolver: yupResolver(
      yup.lazy((_values, { context }) => {
        // Email and API key are only needed when connecting manually instead of via Zendesk OAuth.
        const isManualRequired = Boolean(context?.isManualConnectActive);

        return yup.object({
          subdomain: stringScheme({ required: true }).label('Zendesk API Subdomain'),
          auth_email: emailScheme({
            required: isManualRequired,
            allowEmpty: !isManualRequired,
          }).label('Zendesk email'),
          api_token: isManualRequired
            ? passwordScheme({ required: true, min: 3, max: 100 }).label('Zendesk API Key')
            : stringScheme({ required: false }).label('Zendesk API Key'),
          start_from: momentScheme({ required: true, valid: true }),
          agents: stringScheme({ required: false }),
          tags: stringScheme({ required: false }),
          groups: stringScheme({ required: false }),
          autoSync: booleanScheme({ required: false }).label('AutoSync'),
          interval: numberScheme({ required: false }).label('Interval'),
        });
      }),
    ),
    context: { isManualConnectActive },
  });

  const runImmediately =
    typeof initialRequest.run_immediately === 'undefined'
      ? ZENDESK_TICKETS_INITIAL_REQUEST.run_immediately
      : initialRequest.run_immediately;

  const buildBody = (data) => {
    const subdomain = getSubdomainHelper(data.subdomain)?.subdomain || data.subdomain;
    const name = `${INTEGRATION_NAMES_MAP[data.type]} (${subdomain})`;
    const body: any = {
      ...ZENDESK_TICKETS_INITIAL_REQUEST,
      ...initialRequest,
      // A new object so the shared initial request constant isn't mutated.
      schedule: {
        ...ZENDESK_TICKETS_INITIAL_REQUEST.schedule,
        ...initialRequest.schedule,
        enabled: data.autoSync,
        interval: data.interval,
      },
      start_from: moment(data.start_from).startOf('day').format(ISO_DATE_FORMAT),
      agents: splitString(data.agents),
      tags: splitString(data.tags),
      groups: splitString(data.groups),
      name: generateUniqueName(name, collectorsResponse),
      subdomain,
      run_immediately: runImmediately,
    };

    // Credentials come from Zendesk OAuth unless the manual API token panel is used.
    if (isManualConnectActive) {
      body.auth_email = data.auth_email;
      body.api_token = data.api_token;
    } else {
      delete body.auth_email;
      delete body.api_token;
    }

    return body;
  };

  // Polls the knowledge base collectors until the Zendesk OAuth consent is granted (auth_status "ok").
  // Resolves with the authorized collector, or null on timeout or when the form unmounts.
  const waitForZendeskAuthorization = async (collectorId) => {
    const deadline = Date.now() + AUTH_STATUS_POLL_TIMEOUT_MS;

    while (Date.now() < deadline) {
      await wait(AUTH_STATUS_POLL_INTERVAL_MS);
      if (!isMountedRef.current) return null;

      const { data: collectors } = await getCollectors({ id: kbId, include_data: true });
      if (!isMountedRef.current) return null;

      const collector = collectors?.find(({ id }) => id === collectorId);
      if (collector?.auth_status === 'ok') {
        return collector;
      }
    }

    return null;
  };

  const pollZendeskAuthorization = async (collector) => {
    if (isPollingAuthStatusRef.current) return;
    isPollingAuthStatusRef.current = true;
    setIsCheckingAuthStatus(true);

    const authorizedCollector = await waitForZendeskAuthorization(collector.id);
    isPollingAuthStatusRef.current = false;

    if (!isMountedRef.current) return;
    setIsCheckingAuthStatus(false);

    if (authorizedCollector) {
      setConnectedCollector({ ...collector, ...authorizedCollector });
      notification.success('Zendesk account was connected.');
    } else {
      notification.error('Zendesk authorization timed out. Please try again.');
    }
  };

  const onConnectWithZendesk = async (formData) => {
    let collector = connectedCollector;

    if (!collector) {
      loader.show();
      // Runs after authorization, when Integrate is pressed.
      const { data: createdCollector, error } = await createConnectCollector({
        id: kbId,
        body: { ...buildBody(formData), run_immediately: false },
      });
      loader.hide();

      if (error) return;
      collector = createdCollector;
      setConnectedCollector(createdCollector);
    } else {
      // Zendesk authorizes the collector as stored, so the current values (e.g. an edited
      // subdomain) are saved before requesting consent again.
      loader.show();
      const { error } = await updateCollector({
        knowledgeBaseId: kbId,
        collectorId: collector.id,
        body: { ...buildBody(formData), name: collector.name, run_immediately: false },
      });
      loader.hide();

      if (error) return;
    }

    const { data: consentLink } = await getZendeskConsentLink({
      knowledgeBaseId: kbId,
      collectorId: collector.id,
    });

    if (consentLink?.authorize_url) {
      window.open(consentLink.authorize_url);
      pollZendeskAuthorization(collector);
    }
  };

  // Runs the same validation as submit; the connect handler only fires for a valid form.
  const connectWithZendesk = handleSubmit(onConnectWithZendesk);

  const integrateConnectedCollector = async (formData) => {
    const body = { ...buildBody(formData), name: connectedCollector.name };

    loader.show();
    const { error } = await updateCollector({
      knowledgeBaseId: kbId,
      collectorId: connectedCollector.id,
      body,
    });

    if (!error && runImmediately) {
      await runCollector({ knowledgeBaseId: kbId, collectorId: connectedCollector.id });
    }
    loader.hide();

    if (!error) {
      notification.success(`${connectedCollector.name} Added Successfully.`);
      close(true);
    }
  };

  const submit = (data) => {
    if (!isValid) return;

    if (connectedCollector) {
      return integrateConnectedCollector(data);
    }

    createCollector({ id: kbId, body: buildBody(data) });
  };

  const isAutoSync = watch('autoSync');
  const isIntegrateDisabled = !isManualConnectActive && !isZendeskAuthorized;

  return (
    <form
      onSubmit={handleSubmit(submit)}
      id="zendesk-integration-form-2"
      className={'h-full flex flex-col justify-between'}
    >
      {isCheckingAuthStatus && (
        <div
          className={
            'absolute flex items-center justify-center w-full h-full top-0 left-0 bg-ebony_opacity_30 backdrop-blur-sm z-50'
          }
        >
          <div
            className={
              'flex flex-col items-center gap-4 bg-light p-4 rounded-sm-5 text-sm font-semibold'
            }
          >
            <Spinner className={'!w-4 !border-[3px] !border-r-ebony_opacity_50'} />
            <p>Checking Zendesk Connection...</p>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-5 max-h-[510px] overflow-auto px-4">
        <div className={'flex flex-col gap-2'}>
          <p className={'text-sm font-bold text-ebony'}>Synced tickets</p>
          <Controller
            control={control}
            name="start_from"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <MaskedDateInput
                type={'text'}
                label={'Tickets solved from'}
                infoTooltip={{
                  side: 'right',
                  description: 'Sync all solved tickets from the set date till today',
                }}
                onChange={onChange}
                value={value}
                error={error}
                className={'disabled:opacity-50 px-3 py-2 text-sm'}
              />
            )}
          />
          <Collapsible
            triggerClassName={COLLAPSIBLE_TRIGGER_CLASS_NAME}
            trigger={
              <>
                <span className={'flex-1 text-center'}>Filter Tickets By</span>
                <img
                  src={ChevronDownIcon}
                  alt={''}
                  width={12.8}
                  height={12.8}
                  className={'transition-transform duration-200 group-data-[state=open]:rotate-180'}
                />
              </>
            }
            contentClassName={COLLAPSIBLE_CONTENT_CLASS_NAME}
          >
            <div className={'flex flex-col gap-2 pt-2'}>
              <Controller
                control={control}
                name="agents"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'text'}
                    label={'Tickets solved by'}
                    onChange={onChange}
                    value={value}
                    isValid={!error?.message}
                    invalidMessage={error?.message}
                    placeholder={'agent ID, agent ID, agent ID'}
                    infoTooltip={{
                      side: 'right',
                      description: 'Use agent IDs to ensure precise matching.',
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="tags"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'text'}
                    label={'Tickets tagged with'}
                    onChange={onChange}
                    value={value}
                    isValid={!error?.message}
                    invalidMessage={error?.message}
                    placeholder={'tag name, tag name, tag name'}
                    infoTooltip={{
                      side: 'right',
                      description: 'Enter tag name to filter tickets accurately.',
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="groups"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'text'}
                    label={'Tickets in group'}
                    onChange={onChange}
                    value={value}
                    isValid={!error?.message}
                    invalidMessage={error?.message}
                    placeholder={'group ID, group ID, group ID'}
                    infoTooltip={{
                      side: 'right',
                      description: 'Use group IDs to avoid ambiguity.',
                    }}
                  />
                )}
              />
            </div>
          </Collapsible>
        </div>
        <div className={'flex flex-row items-center gap-5'}>
          <Controller
            control={control}
            name="autoSync"
            render={({ field: { onChange, value } }) => (
              <div className={'flex items-center gap-2'}>
                <Switch
                  type="accent"
                  id="zendesk-tickets-auto-sync"
                  checked={value}
                  onCheckedChange={onChange}
                  disabled={isTrialPlan}
                />
                <label
                  htmlFor="zendesk-tickets-auto-sync"
                  className={'text-sm font-medium text-ebony_opacity_80'}
                >
                  Auto-Sync
                </label>
                {isTrialPlan && (
                  <InputInfoToolTip
                    infoToolTip={{
                      side: 'right',
                      description: <>Unavailable for trial plan</>,
                    }}
                  />
                )}
              </div>
            )}
          />
          {isAutoSync && (
            <Controller
              control={control}
              name="interval"
              render={({ field: { onChange, value } }) => (
                <RadioGroup
                  onValueChange={(interval) => onChange(Number(interval))}
                  defaultValue={String(value)}
                  disabled={isTrialPlan}
                  className="flex flex-row gap-5 items-center"
                >
                  {INTERVAL_OPTIONS.map((option) => (
                    <label
                      key={option.id}
                      htmlFor={option.id}
                      className="flex cursor-pointer items-center gap-2 text-sm font-medium text-tertiary"
                    >
                      <RadioGroupItem
                        value={String(option.value)}
                        id={option.id}
                        className={
                          'border-grey_300 text-green_750 data-[state=checked]:border-green_750'
                        }
                      />
                      {option.label}
                    </label>
                  ))}
                </RadioGroup>
              )}
            />
          )}
        </div>
        <div className={'flex flex-col gap-2'}>
          <p className={'text-sm font-bold text-ebony'}>API Details</p>
          <Controller
            control={control}
            name="subdomain"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'text'}
                label={'Zendesk subdomain'}
                placeholder={'.domain.com'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={(e) => {
                  const res = getSubdomainHelper(e.target.value);
                  if (res?.subdomain) {
                    onChange(res?.subdomain);
                  } else {
                    onChange(e.target.value);
                  }
                }}
                infoTooltip={{ side: 'right', description: 'Link to your Zendesk workspace.' }}
              />
            )}
          />
        </div>
        <ZendeskConnectSection
          isZendeskAuthorized={isZendeskAuthorized}
          isManualConnectActive={isManualConnectActive}
          onConnectWithZendesk={connectWithZendesk}
          onManualConnectOpenChange={setIsManualConnectOpen}
        >
          <Controller
            control={control}
            name="auth_email"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'email'}
                label={'Zendesk email'}
                placeholder={'email@zendesk.com'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                infoTooltip={{
                  side: 'right',
                  description: <>Email address of the Zendesk user used to generate the API key.</>,
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="api_token"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'password'}
                label={'Zendesk API key'}
                placeholder={'------------------------------------------------------'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                infoTooltip={{
                  side: 'right',
                  description: (
                    <>
                      Check how to generate the API Key{' '}
                      <a
                        href={ZENDESK_HELP_CENTER_URL.API_KEY}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline cursor-pointer"
                      >
                        here →
                      </a>
                    </>
                  ),
                }}
              />
            )}
          />
        </ZendeskConnectSection>
      </div>
      <div className="flex justify-center mt-5 px-4">
        <div className="relative flex gap-2">
          <Button variant="tertiary" type="button" className="w-[100px]" onClick={() => close()}>
            Cancel
          </Button>
          <Button variant="dark" type="submit" className="w-[100px]" disabled={isIntegrateDisabled}>
            Integrate
          </Button>
          {backButtonVisible && (
            <Button
              variant="ghost-grey"
              type="button"
              className="absolute left-full ml-8 w-[100px]"
              onClick={prev}
            >
              <img
                src={ArrowLargeGreyIcon}
                alt={''}
                width={16}
                height={16}
                className={'rotate-180'}
              />
              Back
            </Button>
          )}
        </div>
      </div>
    </form>
  );
};
